<template>
	<div class="mini-shop-page">
		<div class="page-head">
			<div class="tab-bar">
				<button
					v-for="item in tabs"
					:key="item.name"
					type="button"
					class="tab-btn"
					:class="{ 'is-active': tab === item.name }"
					@click="switchTab(item.name)"
				>{{ item.label }}</button>
			</div>
			<el-form :inline="true" size="small" class="filter-form" @submit.native.prevent="onRefresh">
				<el-form-item>
					<el-button type="primary" :loading="loading" @click="onRefresh">刷新</el-button>
				</el-form-item>
			</el-form>
		</div>

		<div class="table-wrap">
			<el-table
				:key="tab"
				:data="list"
				size="small"
				height="100%"
				v-loading="loading"
				class="shop-table"
			>
				<el-table-column label="消费者" min-width="180">
					<template slot-scope="scope">
						<div class="buyer-cell">
							<div class="avatar">
								<img v-if="scope.row.buyer_avatar" :src="scope.row.buyer_avatar" alt="" width="32" height="32">
								<i v-else class="el-icon-user"></i>
							</div>
							<div class="buyer-meta">
								<div class="buyer-name">{{ scope.row.buyer_name || '-' }}</div>
								<div class="sub">{{ scope.row.buyer_mobile || '-' }}</div>
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="商品" min-width="200">
					<template slot-scope="scope">
						<div class="goods-preview">
							<div
								v-for="(item, idx) in previewItems(scope.row)"
								:key="idx"
								class="thumb"
							>
								<img v-if="item.product_pic" :src="item.product_pic" alt="" width="48" height="48">
								<i v-else class="el-icon-picture-outline"></i>
							</div>
							<span v-if="itemCount(scope.row)" class="sub">{{ itemCount(scope.row) }} 件</span>
							<span v-else class="sub">-</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="商户订单号" min-width="180" show-overflow-tooltip>
					<template slot-scope="scope">
						<span class="cell-link" @click.stop="copyText(scope.row.out_trade_no)">{{ scope.row.out_trade_no || '-' }}</span>
					</template>
				</el-table-column>
				<el-table-column label="平台单号" min-width="160" show-overflow-tooltip>
					<template slot-scope="scope">{{ scope.row.trade_no || '-' }}</template>
				</el-table-column>
				<el-table-column label="支付状态" width="130" align="center">
					<template slot-scope="scope">
						<span>{{ scope.row.status_text || '-' }}</span>
						<el-tag v-if="isAutoPayPending(scope.row)" size="mini" type="warning" class="pending-tag">待补付</el-tag>
					</template>
				</el-table-column>
				<el-table-column label="履约阶段" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.stage_text || '-' }}</template>
				</el-table-column>
				<el-table-column label="零售金额" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.retail_fee_yuan || '-' }}</template>
				</el-table-column>
				<el-table-column label="收货人" width="90" align="center" show-overflow-tooltip>
					<template slot-scope="scope">{{ scope.row.recv_name || '-' }}</template>
				</el-table-column>
				<el-table-column label="下单时间" width="160" align="center">
					<template slot-scope="scope">{{ scope.row.create_time_str || '-' }}</template>
				</el-table-column>
				<el-table-column label="操作" width="80" align="center" fixed="right">
					<template slot-scope="scope">
						<span class="cell-link" @click="openDetail(scope.row)">详情</span>
					</template>
				</el-table-column>
			</el-table>
		</div>

		<div class="pager">
			<el-pagination
				background
				layout="total, sizes, prev, pager, next"
				:total="total"
				:current-page="page"
				:page-size="limit"
				:page-sizes="pageSizes"
				@current-change="onPageChange"
				@size-change="onSizeChange"
			/>
		</div>

		<el-dialog title="订单详情" :visible.sync="detailVisible" width="920px" append-to-body @close="detail = null">
			<div v-loading="detailLoading">
				<template v-if="detail">
					<div class="section">
						<div class="section-title">订单信息</div>
						<div class="kv-grid">
							<div class="kv">
								<span>商户订单号</span>
								<em class="cell-link" @click="copyText(detail.out_trade_no)">{{ detail.out_trade_no || '-' }}</em>
							</div>
							<div class="kv"><span>平台单号</span><em>{{ detail.trade_no || '-' }}</em></div>
							<div class="kv">
								<span>支付状态</span>
								<em>
									{{ detail.status_text || '-' }}
									<el-tag v-if="isAutoPayPending(detail)" size="mini" type="warning" class="pending-tag">待补付</el-tag>
								</em>
							</div>
							<div class="kv"><span>履约阶段</span><em>{{ detail.stage_text || '-' }}</em></div>
							<div class="kv"><span>下单时间</span><em>{{ detail.create_time_str || '-' }}</em></div>
							<div class="kv"><span>支付时间</span><em>{{ detail.pay_time_str || '-' }}</em></div>
							<div class="kv"><span>关闭时间</span><em>{{ detail.close_time_str || '-' }}</em></div>
						</div>
					</div>

					<div class="section">
						<div class="section-title">消费者</div>
						<div v-if="detail.buyer" class="buyer-cell">
							<div class="avatar">
								<img v-if="detail.buyer.avatar" :src="detail.buyer.avatar" alt="" width="36" height="36">
								<i v-else class="el-icon-user"></i>
							</div>
							<div>
								<div>{{ detail.buyer.nickname || ('用户' + (detail.buyer.uid || '')) }}</div>
								<div class="sub">{{ detail.buyer.mobile || '-' }}</div>
							</div>
						</div>
						<div v-else class="empty-line">-</div>
					</div>

					<div class="section">
						<div class="section-title">收货信息</div>
						<div class="kv-grid">
							<div class="kv"><span>收货人</span><em>{{ detail.recv_name || '-' }}</em></div>
							<div class="kv">
								<span>手机号</span>
								<em>{{ detail.recv_mobile_mask || '-' }}</em>
								<span v-if="detail.recv_mobile" class="cell-link cell-link-gap" @click="copyText(detail.recv_mobile)">复制</span>
							</div>
							<div class="kv kv-block"><span>地址</span><em>{{ detail.recv_address_full || '-' }}</em></div>
							<div class="kv kv-block"><span>备注</span><em>{{ detail.buyer_remark || '-' }}</em></div>
						</div>
					</div>

					<div class="section">
						<div class="section-title">商品明细</div>
						<el-table :data="detail.items || []" size="small" border>
							<el-table-column label="商品" min-width="220">
								<template slot-scope="scope">
									<div class="goods-cell">
										<div class="thumb">
											<img v-if="scope.row.product_pic" :src="scope.row.product_pic" alt="" width="48" height="48">
											<i v-else class="el-icon-picture-outline"></i>
										</div>
										<div>
											<div>{{ scope.row.product_brand || '-' }} {{ scope.row.product_style_number || '' }}</div>
											<div class="sub">尺码 {{ scope.row.product_size || '-' }}</div>
										</div>
									</div>
								</template>
							</el-table-column>
							<el-table-column label="下单数量" width="90" align="center">
								<template slot-scope="scope">{{ scope.row.num || 0 }}</template>
							</el-table-column>
							<el-table-column label="实发数量" width="90" align="center">
								<template slot-scope="scope">{{ scope.row.true_num || 0 }}</template>
							</el-table-column>
							<el-table-column label="零售价" width="100" align="center">
								<template slot-scope="scope">{{ scope.row.retail_price_yuan || '-' }}</template>
							</el-table-column>
						</el-table>
					</div>

					<div class="section">
						<div class="section-title">金额</div>
						<div class="amount-block">
							<div class="amount-item">
								<span>零售</span>
								<em>{{ detail.retail_fee_yuan || '-' }}</em>
							</div>
							<div class="amount-item">
								<span>成本</span>
								<em>{{ detail.cost_fee_yuan || '-' }}</em>
							</div>
							<div class="amount-item is-profit">
								<span>利润</span>
								<em>{{ detail.profit_fee_yuan || '-' }}</em>
							</div>
						</div>
					</div>

					<div class="section">
						<div class="section-title">物流包裹</div>
						<el-table v-if="detail.packages && detail.packages.length" :data="detail.packages" size="small" border>
							<el-table-column label="物流公司" min-width="120">
								<template slot-scope="scope">{{ scope.row.delivery_name || '-' }}</template>
							</el-table-column>
							<el-table-column label="运单号" min-width="160">
								<template slot-scope="scope">
									<span class="cell-link" @click="copyText(scope.row.delivery_num)">{{ scope.row.delivery_num || '-' }}</span>
								</template>
							</el-table-column>
							<el-table-column label="状态" width="100" align="center">
								<template slot-scope="scope">{{ scope.row.delivery_status_text || '-' }}</template>
							</el-table-column>
							<el-table-column label="发货时间" width="160" align="center">
								<template slot-scope="scope">{{ scope.row.delivery_time_str || '-' }}</template>
							</el-table-column>
							<el-table-column label="签收时间" width="160" align="center">
								<template slot-scope="scope">{{ scope.row.qianshou_time_str || '-' }}</template>
							</el-table-column>
						</el-table>
						<div v-else class="empty-line">暂无物流</div>
					</div>

					<div v-if="detail.after_sale" class="section">
						<div class="section-title">售后</div>
						<div class="after-sale-row">
							<span>最新售后 {{ detail.after_sale.status_text || '-' }}</span>
							<span class="cell-link cell-link-gap" @click="openAfterSale(detail.after_sale.service_id)">查看售后</span>
						</div>
					</div>
				</template>
			</div>
		</el-dialog>

		<after-sale-dialog :visible.sync="afterSaleVisible" :service-id="afterSaleId" />
	</div>
</template>

<script>
	import AfterSaleDialog from './afterSaleDialog.vue'
	import {
		MINI_CONFIG_API,
		MINI_SHOP_ORDER_TABS,
		MINI_SHOP_PAGE_SIZES,
		getApiErrorMessage,
	} from '@/constants/retailPlatform'

	export default {
		name: 'MiniprogramOrder',
		components: { AfterSaleDialog },
		data() {
			return {
				tabs: MINI_SHOP_ORDER_TABS,
				pageSizes: MINI_SHOP_PAGE_SIZES,
				tab: 'all',
				page: 1,
				limit: 20,
				total: 0,
				list: [],
				loading: false,
				detailVisible: false,
				detailLoading: false,
				detail: null,
				afterSaleVisible: false,
				afterSaleId: 0,
			}
		},
		mounted() {
			this.loadList()
		},
		methods: {
			isAutoPayPending(row) {
				return row && Number(row.auto_pay_pending) === 1
			},
			previewItems(row) {
				const items = (row && row.items) || []
				return items.slice(0, 3)
			},
			itemCount(row) {
				return ((row && row.items) || []).length
			},
			switchTab(name) {
				if (this.tab === name) {
					return
				}
				this.tab = name
				this.page = 1
				this.loadList()
			},
			onRefresh() {
				this.page = 1
				this.loadList()
			},
			onPageChange(page) {
				this.page = page
				this.loadList()
			},
			onSizeChange(limit) {
				this.limit = limit
				this.page = 1
				this.loadList()
			},
			copyText(value) {
				if (value == null || value === '') {
					this.$message.warning('暂无可复制内容')
					return
				}
				const text = String(value)
				if (navigator.clipboard && navigator.clipboard.writeText) {
					navigator.clipboard.writeText(text).then(() => {
						this.$message.success('已复制')
					}).catch(() => {
						this.fallbackCopy(text)
					})
					return
				}
				this.fallbackCopy(text)
			},
			fallbackCopy(text) {
				const input = document.createElement('textarea')
				input.value = text
				document.body.appendChild(input)
				input.select()
				try {
					document.execCommand('copy')
					this.$message.success('已复制')
				} catch (e) {
					this.$message.error('复制失败')
				}
				document.body.removeChild(input)
			},
			loadList() {
				const params = {
					page: this.page,
					limit: this.limit,
				}
				if (this.tab !== 'all') {
					params.status = Number(this.tab)
				}
				this.loading = true
				this.$axios.get(MINI_CONFIG_API.shopOrderList, {
					params: params,
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.list = []
						this.total = 0
						this.$message.error(getApiErrorMessage(res, '获取订单失败'))
						return
					}
					const data = res.data.data || {}
					this.list = data.list || []
					this.total = Number(data.total) || 0
				}).catch((err) => {
					console.log(err)
					this.list = []
					this.total = 0
					this.$message.error('获取订单失败')
				}).finally(() => {
					this.loading = false
				})
			},
			openDetail(row) {
				if (!row || !row.id) {
					return
				}
				this.detailVisible = true
				this.detailLoading = true
				this.detail = null
				this.$axios.get(MINI_CONFIG_API.shopOrderDetail, {
					params: { mini_pay_id: row.id },
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '获取订单详情失败'))
						return
					}
					this.detail = res.data.data || null
				}).catch((err) => {
					console.log(err)
					this.$message.error('获取订单详情失败')
				}).finally(() => {
					this.detailLoading = false
				})
			},
			openAfterSale(serviceId) {
				if (!serviceId) {
					return
				}
				this.afterSaleId = serviceId
				this.afterSaleVisible = true
			},
		},
	}
</script>

<style scoped lang="less">
	.mini-shop-page {
		height: 100%;
		display: flex;
		flex-direction: column;
		background: #fff;
		padding: 12px 16px 8px;
		box-sizing: border-box;
		overflow: hidden;
	}

	.page-head {
		flex: none;
	}

	.tab-bar {
		display: flex;
		align-items: center;
		border-bottom: 1px solid #ebeef5;
		margin-bottom: 12px;
	}

	.tab-btn {
		position: relative;
		display: inline-flex;
		align-items: center;
		height: 40px;
		padding: 0 16px;
		border: 0;
		background: transparent;
		color: #606266;
		font-size: 14px;
		line-height: 1;
		cursor: pointer;
		outline: none;

		&.is-active {
			color: #ff4714;
			font-weight: 500;

			&::after {
				content: '';
				position: absolute;
				left: 16px;
				right: 16px;
				bottom: 0;
				height: 2px;
				background: #ff4714;
			}
		}
	}

	.filter-form {
		display: flex;
		align-items: center;
		margin-bottom: 12px;

		/deep/ .el-form-item {
			margin: 0 8px 0 0;
		}
	}

	.table-wrap {
		flex: 1;
		min-height: 0;
	}

	.shop-table {
		width: 100%;
	}

	.buyer-cell,
	.goods-cell {
		display: flex;
		align-items: center;
		min-width: 0;
	}

	.avatar,
	.thumb {
		width: 32px;
		height: 32px;
		flex: none;
		margin-right: 8px;
		border-radius: 50%;
		background: #f5f6f8;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 32px;
			height: 32px;
			object-fit: cover;
			display: block;
		}

		i {
			color: #c0c4cc;
		}
	}

	.thumb {
		width: 48px;
		height: 48px;
		border-radius: 4px;
		margin-right: 6px;

		img {
			width: 48px;
			height: 48px;
		}
	}

	/deep/ .thumb img,
	/deep/ .avatar img {
		width: 100% !important;
		height: 100% !important;
		max-width: 48px;
		max-height: 48px;
		object-fit: cover;
		display: block;
	}

	.buyer-meta {
		min-width: 0;
	}

	.buyer-name {
		color: #303133;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.sub {
		margin-top: 2px;
		font-size: 12px;
		color: #909399;
	}

	.goods-preview {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
	}

	.pending-tag {
		margin-left: 4px;
	}

	.cell-link {
		color: #ff4714;
		cursor: pointer;
		font-size: 13px;
	}

	.cell-link-gap {
		margin-left: 8px;
	}

	.pager {
		flex: none;
		padding: 10px 0 4px;
		text-align: right;
	}

	.section {
		margin-bottom: 16px;
	}

	.section-title {
		margin-bottom: 8px;
		font-size: 13px;
		font-weight: 600;
		color: #303133;
	}

	.kv-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 8px 24px;
	}

	.kv {
		min-width: 240px;
		font-size: 13px;
		color: #606266;
		line-height: 1.6;

		span {
			margin-right: 8px;
			color: #909399;
		}

		em {
			font-style: normal;
			color: #303133;
		}
	}

	.kv-block {
		width: 100%;
		min-width: 0;
	}

	.empty-line {
		font-size: 13px;
		color: #909399;
	}

	.amount-block {
		display: flex;
		gap: 24px;
	}

	.amount-item {
		min-width: 120px;

		span {
			display: block;
			font-size: 12px;
			color: #909399;
			margin-bottom: 4px;
		}

		em {
			font-style: normal;
			font-size: 18px;
			color: #303133;
			font-weight: 600;
		}

		&.is-profit em {
			color: #ff4714;
		}
	}

	.after-sale-row {
		font-size: 13px;
		color: #606266;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/miniprogram/order.vue